/**
 * Per-venue manifest injection for the add-to-home-screen feature.
 * 「保存到桌面」的每店 manifest 注入。
 *
 * The static `<link rel="manifest">` was removed from index.html on purpose:
 * iOS 16.4+ reads the manifest's `display` field, and `standalone` would make
 * home-screen saves open in an isolated web app — separate cookies and
 * localStorage, losing the whole vuex-persist state (cart, contact details,
 * language, currency). So the manifest exists ONLY as a runtime injection,
 * and ONLY for Android Chrome, where WebAPK storage is shared with the
 * browser and none of that applies.
 *
 * index.html 里的静态 manifest 引用是刻意删掉的：iOS 16.4+ 会读 manifest 的
 * `display` 字段，standalone 会让保存到主屏的入口进入隔离 web app——cookie 与
 * localStorage 独立，vuex-persist 的购物车/联系方式/语言/货币全部丢失。所以
 * manifest 只以运行时注入的形式存在，且只给 Android Chrome——WebAPK 与浏览器
 * 共享存储，没有上述问题。
 */

import { detectA2hsPlatform, A2HS_PLATFORM } from './platform'

// The injected link carries this id so re-injection updates instead of piling up.
// 注入的 link 带这个 id，重复注入时更新而不是堆积。
export const MANIFEST_LINK_ID = 'a2hs-venue-manifest'

/**
 * Detect the entry family + portal context from the page URL — pure.
 * 从页面 URL 判定入口形态与 portal 上下文——纯函数。
 *
 * The backend reproduces both in the manifest's start_url so the installed
 * icon reopens the SAME ordering mode the customer saved from: `/online/` and
 * `/v/o/` are the takeout family (isOnline behaviours differ from standard
 * pickup), and `portal=true` is a functional param (hides the order-type
 * picker, drives takeaway/counter detection).
 * 后端会把这两项复现进 manifest 的 start_url，让图标打开的就是顾客保存时的
 * 模式：`/online/` 与 `/v/o/` 同属 takeout 形态（isOnline 行为与标准 pickup
 * 不同），`portal=true` 是功能参数（隐藏订单类型选择、判定 counter 环境）。
 *
 * @param {string} pathname - e.g. window.location.pathname
 * @param {string} search - e.g. window.location.search
 * @returns {{ online: boolean, portal: boolean }}
 */
export function detectEntryForm(pathname, search) {
  const path = typeof pathname === 'string' ? pathname : ''
  const query = typeof search === 'string' ? search : ''
  const online = path.indexOf('/online/') === 0 || path.indexOf('/v/o/') === 0
  let portal = false
  try {
    portal = new URLSearchParams(query).get('portal') === 'true'
  } catch (e) {
    portal = false
  }
  return { online, portal }
}

/**
 * Extract the venue identifier segment out of a venue-home path — pure.
 * 从 venue 页路径里提取标识段——纯函数。
 *
 * The customer usually browses under the url SLUG while the canonical venueId
 * is the UUID. The backend uses this segment (validated server-side) for the
 * manifest's start_url/scope so the page the customer is on always sits
 * inside the manifest scope. Unrecognised shapes (read-only /r/, SP mode,
 * anything else) return '' — the backend then falls back to the UUID form.
 * 顾客通常在 slug URL 下浏览，而规范 venueId 是 UUID。后端用这个标识段
 * （服务端校验后）拼 start_url/scope，保证顾客所在页面必然落在 scope 内。
 * 认不出的形态（只读 /r/、SP 模式等）返回 ''，后端回落 UUID 形态。
 *
 * @param {string} pathname - e.g. '/online/v/airlie_pizza_place/home/p'
 * @returns {string} the identifier segment, or '' when unrecognised
 */
export function extractPathVenueSegment(pathname) {
  const path = typeof pathname === 'string' ? pathname : ''
  const seg = path.split('/')
  let raw = ''
  if (seg[1] === 'online' && seg[2] === 'v') {
    raw = seg[3] === 'o' ? (seg[4] || '') : (seg[3] || '')
  } else if (seg[1] === 'v') {
    if (seg[2] === 'o') raw = seg[3] || ''
    else if (seg[2] !== 'sp') raw = seg[2] || ''
  }
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch (e) {
    return ''
  }
}

/**
 * Build the backend manifest URL for a venue. The endpoint lives on the API
 * origin (cross-origin manifests are fine; all URLs inside are absolute).
 * 拼接某店的后端 manifest 地址。接口在 API 域上（跨域 manifest 可行，内部
 * URL 全部为绝对地址）。
 *
 * @param {string} venueId
 * @param {string} [apiBase] - defaults to VUE_APP_BASE_API
 * @param {Object} [entryForm] - from detectEntryForm(); omitted params keep
 *   the standard-form URL, which older backends also understand
 *   省略时保持标准形态 URL，旧后端同样认识
 * @param {boolean} [entryForm.online]
 * @param {boolean} [entryForm.portal]
 * @param {string} [entryForm.pathId] - identifier segment the page is browsed
 *   under; sent only when it differs from venueId (i.e. it is the slug)
 *   页面所用的标识段；仅在与 venueId 不同（即 slug）时携带
 * @returns {string} the URL, or '' when venueId is unusable
 */
export function buildVenueManifestUrl(venueId, apiBase = process.env.VUE_APP_BASE_API, { online = false, portal = false, pathId = '' } = {}) {
  if (!venueId || !apiBase) return ''
  const params = [`id=${encodeURIComponent(venueId)}`]
  if (online) params.push('entry=online')
  if (portal) params.push('portal=1')
  if (pathId && pathId !== venueId) params.push(`pathId=${encodeURIComponent(pathId)}`)
  return `${apiBase}/customer/venue/manifest?${params.join('&')}`
}

/**
 * Inject (or update) the per-venue manifest link — Android Chrome only.
 * Chrome re-evaluates installability when the link appears and then fires
 * `beforeinstallprompt`; call initInstallPromptCapture() BEFORE this.
 * 注入（或更新）每店 manifest link——只在 Android Chrome。link 出现后 Chrome
 * 会重新评估安装条件并触发 `beforeinstallprompt`；调用本函数前先执行
 * initInstallPromptCapture()。
 *
 * @param {string} venueId
 * @param {Object} [options]
 * @param {string} [options.userAgent] - injectable for tests / 测试用注入
 * @param {string} [options.apiBase] - injectable for tests / 测试用注入
 * @param {string} [options.pathname] - injectable for tests; defaults to the live page path
 * @param {string} [options.search] - injectable for tests; defaults to the live query string
 * @returns {boolean} whether the link is now present / link 是否已就位
 */
export function injectVenueManifest(venueId, { userAgent, apiBase, pathname, search } = {}) {
  if (typeof document === 'undefined') return false

  const ua = userAgent !== undefined
    ? userAgent
    : (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  if (detectA2hsPlatform(ua) !== A2HS_PLATFORM.ANDROID_CHROME) return false

  const loc = typeof window !== 'undefined' && window.location ? window.location : null
  const path = pathname !== undefined ? pathname : (loc ? loc.pathname : '')
  const query = search !== undefined ? search : (loc ? loc.search : '')

  const entryForm = detectEntryForm(path, query)
  entryForm.pathId = extractPathVenueSegment(path)
  const url = buildVenueManifestUrl(venueId, apiBase, entryForm)
  if (!url) return false

  let link = document.getElementById(MANIFEST_LINK_ID)
  if (!link) {
    link = document.createElement('link')
    link.id = MANIFEST_LINK_ID
    link.rel = 'manifest'
    document.head.appendChild(link)
  }
  if (link.getAttribute('href') !== url) {
    link.setAttribute('href', url)
  }
  return true
}

/** Remove the injected link (venue switch cleanup / tests). 移除注入的 link。 */
export function removeVenueManifest() {
  if (typeof document === 'undefined') return
  const link = document.getElementById(MANIFEST_LINK_ID)
  if (link && link.parentNode) {
    link.parentNode.removeChild(link)
  }
}
